import type { HttpErrorResponse } from '@angular/common/http';
import type { FamilyPlanMemberView, FamilyPlanView } from '../types/family-plan-view.types';
import type { FamilyFormOption } from '../types/family-member-form.types';

/** Pure rules for taking a plate out of a family from the Family Plan tab (CWS-1815). */

const MESSAGES: Record<string, string> = {
  FAMILY_TOO_SMALL: 'A family with one plate cannot remove it.',
  FAMILY_MEMBER_NOT_FOUND: 'This plate is no longer in the family. Refresh and try again.',
  FAMILY_GROUP_NOT_FOUND: 'This family plan could not be found. Refresh and try again.',
  FAMILY_MEMBER_PHONE_REQUIRED:
    'This plate has no phone number of its own. Add one before taking it out of the family.',
  FAMILY_NEW_PRIMARY_REQUIRED: 'Pick which member becomes the primary.',
  FAMILY_NEW_PRIMARY_PHONE_REQUIRED: 'The new primary needs a phone number of their own.',
  // The card cases need a card saved and a code from the new primary; not built into this tab yet.
  FAMILY_CARD_REQUIRED:
    'This removal needs a new card saved for the plates on the primary card, which this screen cannot do yet.',
  OTP_REQUIRED:
    'This removal moves the primary card to the new primary and needs their code, which this screen cannot do yet.',
};

/** The message for a refusal; the server's own text is the last resort. */
export function familyRemoveErrorMessage(error: unknown): string {
  const http = error as HttpErrorResponse | null;
  const code = http?.error?.data?.code;
  return (
    (typeof code === 'string' ? MESSAGES[code] : undefined) ??
    http?.error?.message ??
    'The plate could not be removed. Check your connection and try again.'
  );
}

/** Who can take over when the primary leaves: another plate with a phone of its own. */
export function newPrimaryCandidates(
  view: FamilyPlanView | null,
  leaving: FamilyPlanMemberView
): FamilyPlanMemberView[] {
  if (leaving.role !== 'primary') {
    return [];
  }
  return (view?.members ?? []).filter(
    member => member.member_id !== leaving.member_id && member.customer.id !== leaving.customer.id
  );
}

/** The picker's options: whose plate, and who. */
export function newPrimaryOptions(candidates: FamilyPlanMemberView[]): FamilyFormOption[] {
  return candidates.map(member => ({
    value: member.member_id,
    label: [member.vehicle.license_plate, member.customer.name].filter(Boolean).join(' · '),
  }));
}

/** The pick is only needed when more than one plate stays; with one left it becomes the primary. */
export function needsNewPrimaryPick(
  view: FamilyPlanView | null,
  leaving: FamilyPlanMemberView
): boolean {
  return leaving.role === 'primary' && (view?.group?.active_count ?? 0) > 2;
}

/** Remove member is offered when the family has a plate to keep: at least two. */
export function canRemoveMember(view: FamilyPlanView | null): boolean {
  return (view?.group?.active_count ?? 0) >= 2;
}

/**
 * Remove member is offered on a plate that has a phone of its own. A plate that shares the
 * primary's customer has none, the API refuses it and the locked edit form cannot add one.
 */
export function canRemoveRow(view: FamilyPlanView | null, contactLocked: boolean): boolean {
  return canRemoveMember(view) && !contactLocked;
}
